import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Box, Typography } from '@mui/material';
import { CURSOR_THROTTLE_MS, CursorPosition } from '../../../hooks/useRetroSync';

const CURSOR_COLORS = [
  '#e74c3c', '#2980b9', '#27ae60', '#8e44ad',
  '#e67e22', '#16a085', '#c0392b', '#2c3e50',
];

/** Hide a remote cursor when no packet arrives for this long. */
export const CURSOR_IDLE_MS = 2500;

function colorForPlayer(playerId: string): string {
  let hash = 0;
  for (let i = 0; i < playerId.length; i++) {
    hash = playerId.charCodeAt(i) + ((hash << 5) - hash);
  }
  return CURSOR_COLORS[Math.abs(hash) % CURSOR_COLORS.length];
}

interface RemoteCursor extends CursorPosition {
  /** False on first sighting so the cursor snaps instead of flying in from 0,0. */
  ready: boolean;
  /** True while interpolating to a new point — drives will-change. */
  moving: boolean;
}

export interface CursorOverlayHandle {
  handleMoved: (cursor: CursorPosition) => void;
  handleLeft: (playerId: string) => void;
}

export interface CursorOverlayProps {
  currentPlayerId: string;
}

const CursorOverlay = forwardRef<CursorOverlayHandle, CursorOverlayProps>(
  function CursorOverlay({ currentPlayerId }, ref) {
    const [cursors, setCursors] = useState<RemoteCursor[]>([]);
    const cursorsRef = useRef<RemoteCursor[]>([]);
    const idleTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
    const movingTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
    const pendingMovesRef = useRef<Map<string, CursorPosition>>(new Map());
    const rafRef = useRef<number | null>(null);
    const currentPlayerIdRef = useRef(currentPlayerId);
    currentPlayerIdRef.current = currentPlayerId;

    const replaceCursors = useCallback((next: RemoteCursor[]) => {
      cursorsRef.current = next;
      setCursors(next);
    }, []);

    const clearIdle = useCallback((playerId: string) => {
      const timer = idleTimersRef.current.get(playerId);
      if (timer) {
        clearTimeout(timer);
        idleTimersRef.current.delete(playerId);
      }
    }, []);

    const clearMoving = useCallback((playerId: string) => {
      const timer = movingTimersRef.current.get(playerId);
      if (timer) {
        clearTimeout(timer);
        movingTimersRef.current.delete(playerId);
      }
    }, []);

    const scheduleIdle = useCallback((playerId: string) => {
      clearIdle(playerId);
      const timer = setTimeout(() => {
        idleTimersRef.current.delete(playerId);
        pendingMovesRef.current.delete(playerId);
        replaceCursors(cursorsRef.current.filter(c => c.playerId !== playerId));
      }, CURSOR_IDLE_MS);
      idleTimersRef.current.set(playerId, timer);
    }, [clearIdle, replaceCursors]);

    const scheduleMovingClear = useCallback((playerId: string) => {
      clearMoving(playerId);
      const timer = setTimeout(() => {
        movingTimersRef.current.delete(playerId);
        const cur = cursorsRef.current.find(c => c.playerId === playerId);
        if (!cur?.moving) return;
        replaceCursors(cursorsRef.current.map(c => (
          c.playerId === playerId ? { ...c, moving: false } : c
        )));
      }, CURSOR_THROTTLE_MS);
      movingTimersRef.current.set(playerId, timer);
    }, [clearMoving, replaceCursors]);

    const flushPending = useCallback(() => {
      rafRef.current = null;
      const pending = pendingMovesRef.current;
      if (pending.size === 0) return;
      const batch = Array.from(pending.values());
      pending.clear();

      const byId = new Map(cursorsRef.current.map(c => [c.playerId, c]));
      const movedIds: string[] = [];
      for (const cursor of batch) {
        if (!cursor.playerId || cursor.playerId === currentPlayerIdRef.current) continue;
        const existing = byId.get(cursor.playerId);
        if (existing) movedIds.push(cursor.playerId);
        byId.set(cursor.playerId, {
          ...cursor,
          ready: !!existing,
          moving: !!existing,
        });
      }
      replaceCursors(Array.from(byId.values()));
      movedIds.forEach(scheduleMovingClear);
    }, [replaceCursors, scheduleMovingClear]);

    const scheduleFlush = useCallback(() => {
      if (rafRef.current != null) return;
      rafRef.current = requestAnimationFrame(flushPending);
    }, [flushPending]);

    const handleMoved = useCallback((cursor: CursorPosition) => {
      if (!cursor?.playerId || cursor.playerId === currentPlayerId) return;
      pendingMovesRef.current.set(cursor.playerId, cursor);
      scheduleIdle(cursor.playerId);
      scheduleFlush();
    }, [currentPlayerId, scheduleFlush, scheduleIdle]);

    const handleLeft = useCallback((playerId: string) => {
      if (!playerId) return;
      pendingMovesRef.current.delete(playerId);
      if (pendingMovesRef.current.size === 0 && rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      clearIdle(playerId);
      clearMoving(playerId);
      replaceCursors(cursorsRef.current.filter(c => c.playerId !== playerId));
    }, [clearIdle, clearMoving, replaceCursors]);

    useImperativeHandle(ref, () => ({ handleMoved, handleLeft }), [handleMoved, handleLeft]);

    useEffect(() => {
      if (!cursors.some(c => !c.ready)) return;
      const frame = requestAnimationFrame(() => {
        replaceCursors(cursorsRef.current.map(c => (c.ready ? c : { ...c, ready: true })));
      });
      return () => cancelAnimationFrame(frame);
    }, [cursors, replaceCursors]);

    useEffect(() => () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      idleTimersRef.current.forEach(timer => clearTimeout(timer));
      idleTimersRef.current.clear();
      movingTimersRef.current.forEach(timer => clearTimeout(timer));
      movingTimersRef.current.clear();
    }, []);

    return (
      <>
        {cursors.map(cursor => {
          const color = colorForPlayer(cursor.playerId);
          return (
            <Box
              key={cursor.playerId}
              data-testid={`remote-cursor-${cursor.playerId}`}
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                pointerEvents: 'none',
                zIndex: 9999,
                transform: `translate3d(${cursor.x - 2}px, ${cursor.y - 2}px, 0)`,
                transition: cursor.ready ? `transform ${CURSOR_THROTTLE_MS}ms linear` : 'none',
                willChange: cursor.moving ? 'transform' : 'auto',
              }}
            >
              <svg width="16" height="20" viewBox="0 0 16 20">
                <path
                  d="M0 0 L0 16 L4 12 L7 19 L9 18 L6 11 L11 11 Z"
                  fill={color}
                  stroke="white"
                  strokeWidth="1"
                />
              </svg>
              <Box
                sx={{
                  backgroundColor: color,
                  color: 'white',
                  borderRadius: '4px',
                  padding: '1px 6px',
                  fontSize: 11,
                  fontWeight: 600,
                  fontFamily: 'sans-serif',
                  whiteSpace: 'nowrap',
                  marginLeft: '12px',
                  marginTop: '-8px',
                }}
              >
                <Typography variant="caption" sx={{ color: 'white', fontWeight: 700, fontSize: 11 }}>
                  {cursor.playerName}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </>
    );
  },
);

export default CursorOverlay;
